import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogActions,
} from "../../../../design/dialog";
import { Button } from "../../../../design/button";
import { Text } from "../../../../design/text";
import { Visit, EventType } from "@visitproof/types";
import { EventService } from "../../../../services/EventService";
import { useAuth } from "../../../../contexts/AuthContext";
import { recordWebTelemetry } from "../../../../utils/telemetry";
import {
  EyeIcon,
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import {
  EyeIcon as EyeIconSolid,
  ChatBubbleLeftRightIcon as ChatIconSolid,
  ExclamationTriangleIcon as WarningIconSolid,
} from "@heroicons/react/24/solid";

interface CreateEventDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onEventCreated: () => void;
  visit: Visit;
}

type UserEventType = "observation" | "interaction" | "incident";

interface EventTypeOption {
  type: UserEventType;
  label: string;
  description: string;
  icon: React.ElementType;
  iconSolid: React.ElementType;
  borderColor: string;
  bgColor: string;
  iconColor: string;
  iconBg: string;
}

const eventTypeOptions: EventTypeOption[] = [
  {
    type: "observation",
    label: "Observation",
    description: "Document what you observe during the visit",
    icon: EyeIcon,
    iconSolid: EyeIconSolid,
    borderColor: "border-blue-500",
    bgColor: "bg-blue-50 dark:bg-blue-900/20",
    iconColor: "text-blue-600 dark:text-blue-400",
    iconBg: "bg-blue-100 dark:bg-blue-900/40",
  },
  {
    type: "interaction",
    label: "Interaction",
    description: "Record a positive interaction between parent and child",
    icon: ChatBubbleLeftRightIcon,
    iconSolid: ChatIconSolid,
    borderColor: "border-green-500",
    bgColor: "bg-green-50 dark:bg-green-900/20",
    iconColor: "text-green-600 dark:text-green-400",
    iconBg: "bg-green-100 dark:bg-green-900/40",
  },
  {
    type: "incident",
    label: "Incident",
    description: "Report a concerning incident or behavior",
    icon: ExclamationTriangleIcon,
    iconSolid: WarningIconSolid,
    borderColor: "border-red-500",
    bgColor: "bg-red-50 dark:bg-red-900/20",
    iconColor: "text-red-600 dark:text-red-400",
    iconBg: "bg-red-100 dark:bg-red-900/40",
  },
];

interface SeverityOption {
  value: string;
  label: string;
  description: string;
  borderColor: string;
  bgColor: string;
  textColor: string;
  dotColor: string;
}

const severityOptions: SeverityOption[] = [
  {
    value: "low",
    label: "Low",
    description: "Minor concern, no immediate action required",
    borderColor: "border-zinc-400",
    bgColor: "bg-zinc-100 dark:bg-zinc-700",
    textColor: "text-zinc-700 dark:text-zinc-300",
    dotColor: "bg-zinc-400",
  },
  {
    value: "medium",
    label: "Medium",
    description: "Moderate concern, may need follow-up",
    borderColor: "border-amber-500",
    bgColor: "bg-amber-50 dark:bg-amber-900/20",
    textColor: "text-amber-700 dark:text-amber-400",
    dotColor: "bg-amber-500",
  },
  {
    value: "high",
    label: "High",
    description: "Serious concern, requires immediate attention",
    borderColor: "border-red-500",
    bgColor: "bg-red-50 dark:bg-red-900/20",
    textColor: "text-red-700 dark:text-red-400",
    dotColor: "bg-red-500",
  },
];

const CreateEventDialog: React.FC<CreateEventDialogProps> = ({
  isOpen,
  onClose,
  onEventCreated,
  visit,
}) => {
  const { user } = useAuth();
  const [step, setStep] = useState<1 | 2>(1);
  const [eventType, setEventType] = useState<UserEventType | null>(null);
  const [body, setBody] = useState("");
  const [severity, setSeverity] = useState("low");
  const [actionTaken, setActionTaken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedOption = eventTypeOptions.find((o) => o.type === eventType);
  const selectedSeverity = severityOptions.find((o) => o.value === severity);

  const handleSubmit = async () => {
    if (!eventType || !user) return;

    try {
      setSubmitting(true);
      setError(null);

      await EventService.createEvent({
        type: eventType as EventType,
        body: body.trim() || undefined,
        severity: eventType === "incident" ? severity : "low",
        actionTaken:
          eventType === "incident" && actionTaken.trim()
            ? actionTaken.trim()
            : null,
        visitId: visit.id,
        caseId: visit.caseId,
        accountId: visit.accountId!,
        createdBy: user.uid,
        // REQUIRED for the events create rule to allow a monitor's note write.
        // The visit's supervisorId is this monitor's id for their assigned visit.
        supervisorId: visit.supervisorId,
      });

      // Reset form
      setStep(1);
      setEventType(null);
      setBody("");
      setSeverity("low");
      setActionTaken("");

      onEventCreated();
    } catch (err: any) {
      console.error("Error creating event:", err);
      setError(err instanceof Error ? err.message : "Failed to create event");
      recordWebTelemetry("error", "monitor_create_note_failed", {
        code: err?.code || "unknown",
        message: err?.message?.slice(0, 200),
        visitId: visit.id,
        eventType,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setStep(1);
      setEventType(null);
      setBody("");
      setSeverity("low");
      setActionTaken("");
      setError(null);
      onClose();
    }
  };

  const handleCategorySelect = (type: UserEventType) => {
    setEventType(type);
    // Auto-advance to step 2 after a short delay for visual feedback
    setTimeout(() => {
      setStep(2);
    }, 300);
  };

  const handleBack = () => {
    setStep(1);
  };

  const getPlaceholder = () => {
    switch (eventType) {
      case "observation":
        return "What did you observe? Describe behaviors, interactions, or noteworthy moments...";
      case "interaction":
        return "Describe the positive interaction. What did the parent and child do together?";
      case "incident":
        return "Describe what happened. Include details about who was involved and what occurred...";
      default:
        return "Describe the event...";
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} size="2xl">
      <DialogTitle>
        {step === 1 ? "Add Event" : `Add ${selectedOption?.label}`}
      </DialogTitle>
      <DialogDescription>
        {step === 1
          ? "Choose the type of event you want to document"
          : "Provide details about the event"}
      </DialogDescription>

      <DialogBody className="space-y-6">
        {/* Error Message */}
        {error && (
          <div className="flex items-start gap-3 p-4 border border-red-200 rounded-xl bg-red-50 dark:border-red-800 dark:bg-red-900/20">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            <Text className="text-red-700 dark:text-red-400">{error}</Text>
          </div>
        )}

        {/* STEP 1: Event Type Selection */}
        {step === 1 && (
        <div>
          <div className="grid gap-4 sm:grid-cols-3">
            {eventTypeOptions.map((option) => {
              const Icon = option.icon;
              const IconSolid = option.iconSolid;
              const isSelected = eventType === option.type;

              return (
                <button
                  key={option.type}
                  type="button"
                  onClick={() => handleCategorySelect(option.type)}
                  className={`relative flex flex-col items-center p-6 text-center border-2 rounded-2xl transition-all duration-200 min-h-[200px] ${
                    isSelected
                      ? `${option.borderColor} ${option.bgColor} ring-4 ring-opacity-20 ${option.borderColor.replace('border-', 'ring-')}`
                      : "border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600 hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                  }`}
                >
                  {/* Selection Indicator */}
                  {isSelected && (
                    <div className="absolute top-3 right-3">
                      <CheckCircleIcon className={`w-6 h-6 ${option.iconColor}`} />
                    </div>
                  )}

                  {/* Icon */}
                  <div
                    className={`flex items-center justify-center w-16 h-16 mb-4 rounded-full transition-colors ${
                      isSelected ? option.iconBg : "bg-zinc-100 dark:bg-zinc-800"
                    }`}
                  >
                    {isSelected ? (
                      <IconSolid className={`w-8 h-8 ${option.iconColor}`} />
                    ) : (
                      <Icon className="w-8 h-8 text-zinc-400 dark:text-zinc-500" />
                    )}
                  </div>

                  {/* Label */}
                  <Text
                    className={`text-lg font-semibold mb-2 ${
                      isSelected ? option.iconColor : "text-zinc-700 dark:text-zinc-300"
                    }`}
                  >
                    {option.label}
                  </Text>

                  {/* Description */}
                  <Text className="text-sm leading-relaxed text-zinc-500 dark:text-zinc-400">
                    {option.description}
                  </Text>
                </button>
              );
            })}
          </div>
        </div>
        )}

        {/* STEP 2: Form Fields */}
        {step === 2 && eventType && (
          <div className="space-y-5">
            {/* Description Field */}
            <div>
              <label
                htmlFor="event-body"
                className="block mb-2 text-sm font-medium text-zinc-900 dark:text-zinc-100"
              >
                Description
                {eventType !== "incident" && (
                  <span className="ml-1 font-normal text-zinc-400">(optional)</span>
                )}
              </label>
              <div className="relative">
                <textarea
                  id="event-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={4}
                  placeholder={getPlaceholder()}
                  className="w-full px-4 py-3 text-sm transition-colors bg-white border resize-none rounded-xl border-zinc-300 dark:border-zinc-600 dark:bg-zinc-800 text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                  maxLength={2000}
                />
                <div className="absolute bottom-3 right-3">
                  <Text
                    className={`text-xs ${
                      body.length > 1800 ? "text-amber-500" : "text-zinc-400"
                    }`}
                  >
                    {body.length}/2000
                  </Text>
                </div>
              </div>
            </div>

            {/* Incident-specific fields */}
            {eventType === "incident" && (
              <>
                {/* Severity Selection */}
                <div>
                  <label className="block mb-2 text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    Severity Level
                  </label>
                  <div className="flex gap-2">
                    {severityOptions.map((option) => {
                      const isSelected = severity === option.value;

                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setSeverity(option.value)}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-xl border-2 transition-all duration-200 ${
                            isSelected
                              ? `${option.borderColor} ${option.bgColor} ${option.textColor}`
                              : "border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800"
                          }`}
                        >
                          <span
                            className={`w-2 h-2 rounded-full ${
                              isSelected ? option.dotColor : "bg-zinc-300 dark:bg-zinc-600"
                            }`}
                          />
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                  {selectedSeverity && (
                    <Text className="mt-2 text-xs text-zinc-500">
                      {selectedSeverity.description}
                    </Text>
                  )}
                </div>

                {/* Action Taken */}
                <div>
                  <label
                    htmlFor="action-taken"
                    className="block mb-2 text-sm font-medium text-zinc-900 dark:text-zinc-100"
                  >
                    Action Taken
                    <span className="ml-1 font-normal text-zinc-400">(optional)</span>
                  </label>
                  <textarea
                    id="action-taken"
                    value={actionTaken}
                    onChange={(e) => setActionTaken(e.target.value)}
                    rows={2}
                    placeholder="What steps did you take in response to this incident?"
                    className="w-full px-4 py-3 text-sm transition-colors bg-white border resize-none rounded-xl border-zinc-300 dark:border-zinc-600 dark:bg-zinc-800 text-zinc-900 dark:text-white placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                  />
                </div>
              </>
            )}
          </div>
        )}
      </DialogBody>

      <DialogActions>
        {step === 1 ? (
          <>
            <Button plain onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Button plain onClick={handleBack} disabled={submitting}>
              Back
            </Button>
            <Button
              color={
                eventType === "incident"
                  ? "red"
                  : eventType === "interaction"
                  ? "green"
                  : "blue"
              }
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Creating...
                </span>
              ) : (
                `Add ${selectedOption?.label || "Event"}`
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateEventDialog;
